import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAnimal, useAuth, useAnimalIntakeRecord, upsertAnimalIntakeRecord } from '@rehome/shared';
import { ArrowLeft, ShieldOff, Loader2, Save } from 'lucide-react';

// ─── AnimalIntakeDetailsPage ────────────────────────────────────────────────
// Own page (was an inline collapsible panel on AnimalProfilePage — moved out
// to declutter the profile). /shelter/:shelterId/animals/:animalId/intake-details
// Staff-only, never public — see animal_intake_records (028_intake_refinement.sql).
// ===========================================================================

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white disabled:bg-gray-50 disabled:text-gray-500';
const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

/**
 * `embedded`: az állat adatlapjának egyik füleként jelenik meg, nem önálló
 * képernyőként. Ilyenkor elmarad az oldal-keret (vissza-gomb, cím) — azt a
 * befoglaló adatlap adja.
 */
const AnimalIntakeDetailsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { shelterId, animalId } = useParams<{ shelterId: string; animalId: string }>();
  const navigate = useNavigate();
  const { animal, loading } = useAnimal(animalId);
  const { userProfile } = useAuth();
  const { data: intakeRecord, refetch: refetchIntakeRecord } = useAnimalIntakeRecord(animalId);

  const [intakeForm, setIntakeForm] = useState({
    circumstances: '', chipOwnerInfo: '', intakeReferenceNumber: '', finderName: '', finderPhone: '',
  });
  const [savingIntakeDetails, setSavingIntakeDetails] = useState(false);

  useEffect(() => {
    setIntakeForm({
      circumstances: intakeRecord?.circumstances || '',
      chipOwnerInfo: intakeRecord?.chipOwnerInfo || '',
      intakeReferenceNumber: intakeRecord?.intakeReferenceNumber || '',
      finderName: intakeRecord?.finderName || '',
      finderPhone: intakeRecord?.finderPhone || '',
    });
  }, [intakeRecord]);

  const canEdit = userProfile?.role === 'shelter_admin' || userProfile?.role === 'super_admin';

  const handleSaveIntakeDetails = async () => {
    if (!animalId) return;
    setSavingIntakeDetails(true);
    await upsertAnimalIntakeRecord(animalId, {
      circumstances: intakeForm.circumstances.trim() || null,
      chipOwnerInfo: intakeForm.chipOwnerInfo.trim() || null,
      intakeReferenceNumber: intakeForm.intakeReferenceNumber.trim() || null,
      finderName: intakeForm.finderName.trim() || null,
      finderPhone: intakeForm.finderPhone.trim() || null,
    });
    setSavingIntakeDetails(false);
    refetchIntakeRecord();
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="animate-pulse h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!animal) return <div className="p-8 text-center text-gray-500">Állat nem található.</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'max-w-3xl mx-auto space-y-6 pb-12'}>
{!embedded && (<>
      <button onClick={() => navigate(`/shelter/${shelterId}/animals/${animalId}`)} className="flex items-center gap-2 text-gray-500 hover:text-teal-700 transition-colors">
        <ArrowLeft className="w-4 h-4" />Vissza {animal.name} profiljához
      </button>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldOff className="w-6 h-6 text-amber-600" />
          Bekerülési részletek — {animal.name}
          <span className="text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">nem publikus</span>
        </h1>
        <p className="text-gray-600 mt-1">Érzékeny adatok, amiket az örökbefogadói felület sosem mutat meg</p>
      </div>
      </>)}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Ez az adatkör (körülmények, chip tulajdonos adata, hatósági iktatószám, megtaláló elérhetősége)
          soha nem jelenik meg az örökbefogadói felületen — külön, staff-only táblában tárolódik.
        </p>
        <div>
          <label className={labelCls}>Körülmények leírása</label>
          <textarea
            value={intakeForm.circumstances}
            onChange={e => setIntakeForm(prev => ({ ...prev, circumstances: e.target.value }))}
            rows={3} className={inputCls} placeholder="Pl. udvaron találtuk, megkötözve..." disabled={!canEdit}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Chip tulajdonos info</label>
            <input type="text" value={intakeForm.chipOwnerInfo} onChange={e => setIntakeForm(prev => ({ ...prev, chipOwnerInfo: e.target.value }))} className={inputCls} placeholder="Tulajdonos neve" disabled={!canEdit} />
          </div>
          <div>
            <label className={labelCls}>Hatósági iktatószám</label>
            <input type="text" value={intakeForm.intakeReferenceNumber} onChange={e => setIntakeForm(prev => ({ ...prev, intakeReferenceNumber: e.target.value }))} className={inputCls} placeholder="pl. jegyzőkönyvszám" disabled={!canEdit} />
          </div>
          <div />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Megtaláló neve</label>
            <input type="text" value={intakeForm.finderName} onChange={e => setIntakeForm(prev => ({ ...prev, finderName: e.target.value }))} className={inputCls} disabled={!canEdit} />
          </div>
          <div>
            <label className={labelCls}>Megtaláló telefonja</label>
            <input type="tel" value={intakeForm.finderPhone} onChange={e => setIntakeForm(prev => ({ ...prev, finderPhone: e.target.value }))} className={inputCls} disabled={!canEdit} />
          </div>
        </div>
        {canEdit && (
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button
              onClick={handleSaveIntakeDetails}
              disabled={savingIntakeDetails}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {savingIntakeDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Bekerülési részletek mentése
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnimalIntakeDetailsPage;
